"use client"

import { useEffect } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { AnalyticsBrowser } from "@segment/analytics-next"
import { v7 as uuidv7 } from "uuid"

import type {
  ChatSideAnalyticsValue,
  ChatSurfaceAnalyticsValue,
} from "@/lib/chat/chat-surface-analytics"
import { isInternalEmail } from "@/lib/internal-domains"
import { type TeamHelpValueKind } from "@/lib/team-help-value"
import {
  type BoardSettings,
  type ViewMode,
} from "@/services/explorer-views/explorer-views.types"
import { type GoalType } from "@/services/okrs/okrs.types"
import {
  type CurrentAccount,
  type CurrentUser,
} from "@/services/users/user.types"

// Get the write key from window.settings
const segmentWriteKey =
  typeof window === "undefined" ? undefined : window.settings?.SEGMENT_WRITE_KEY

const analytics = segmentWriteKey
  ? AnalyticsBrowser.load(
      { writeKey: segmentWriteKey },
      {
        integrations: {
          // Default-deny new destinations; retain Segment transport for cloud mappings.
          All: false,
          "Segment.io": true,
          "Mixpanel (Actions)": true,
          "Google Enhanced Conversions": true,
          // The Google Ads browser plugin checks this alias before enriching events.
          "Google Ads Conversions": true,
          "Hubspot Cloud Mode (actions)": true,
        },
      }
    )
  : null

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return ""
  let sessionId = sessionStorage.getItem("rhythms_session_id")
  if (!sessionId) {
    sessionId = uuidv7()
    sessionStorage.setItem("rhythms_session_id", sessionId)
  }
  return sessionId
}

let tenantContext: {
  tenant_uuid: string
  tenant_name: string
  plg_onboarded: boolean
  internal: boolean
} | null = null

function getGlobalProperties(): Record<string, unknown> {
  return {
    session_id: getOrCreateSessionId(),
    platform: "web",
    // Lets one Segment/Mixpanel project split usage by deployment (dev1,
    // demo, usprod) without relying on per-environment write keys.
    environment:
      typeof window === "undefined" ? undefined : window.settings?.ENV_NAME,
    ...tenantContext,
  }
}

export enum EventName {
  LibraryOrganization = "Library Organization",
  OTPVerificationPageViewed = "OTP verification page viewed",
  OTPSubmitted = "OTP submitted",
  FreeTrialSignup = "Free Trial Signup",
  UserLoggedIn = "User Logged In",
  OKRsImportInitiated = "OKRs Import Initiated",
  OKRsImportCompleted = "OKRs Import Completed",
  OKRsImportFailed = "OKRs Import Failed",
  CascadeInitiated = "Cascade Initiated",
  QuickViewOpened = "Quick View Opened",
  GoalDeleted = "Goal Deleted",
  GoalDiscarded = "Goal Discarded",
  GoalArchived = "Goal Archived",
  GoalUnarchived = "Goal Unarchived",
  GoalRestored = "Goal Restored",
  GoalPublished = "Goal Published",
  GoalApprovalRequested = "Goal Approval Requested",
  OKRViewDisplayed = "OKR View Displayed",
  OKRDisplayModeChanged = "OKR Display Mode Changed",
  SearchResultsViewed = "Search Results Viewed",
  SearchResultClicked = "Search Result Clicked",
  ActivityFeedViewed = "Activity Feed Viewed",
  ActivityFeedLoadMore = "Activity Feed Load More",
  ActivityFeedItemExpanded = "Activity Feed Item Expanded",
  ChatModelSwitched = "Chat Model Switched",
  ChatAutoPromptSent = "Chat Auto-Prompt Sent",
  AIChatMessageSent = "AI Chat Message Sent",
  AIChatMessageQueueAction = "AI Chat Message Queue Action",
  ChatLayoutObserved = "Chat Layout Observed",
  ChatSideChanged = "Chat Side Changed",
  ChatFullViewEntered = "Chat Full View Entered",
  ChatAllChatsOpened = "Chat All Chats Opened",
  ChatFullViewNavigationUsed = "Chat Full View Navigation Used",
  ChatEntryPointClicked = "Chat Entry Point Clicked",
  ChatStarterSelected = "Chat Starter Selected",
  ChatStartersShown = "Chat Starters Shown",
  ChatStarterSent = "Chat Starter Sent",
  ChatSidebarExpandClicked = "Chat Sidebar Expand Clicked",
  ChatFullViewToggleClicked = "Chat Full View Toggle Clicked",
  ChatMessageCopied = "Chat Message Copied",
  ChatSearchFocused = "Chat Search Focused",
  ChatSearchQueried = "Chat Search Queried",
  ChatSearchResultClicked = "Chat Search Result Clicked",
  DashboardVersionViewed = "Dashboard Version Viewed",
  DashboardVersionRestored = "Dashboard Version Restored",
  ActivityLogOpened = "Activity Log Opened",
  ActivityLogEntryExpanded = "Activity Log Entry Expanded",
  DashboardViewed = "Dashboard Viewed",
  DashboardListViewed = "Dashboard List Viewed",
  DashboardContentEdited = "Dashboard Content Edited",
  DashboardChartEdited = "Dashboard Chart Edited",
  DashboardExported = "Dashboard Exported",
  DashboardDuplicated = "Dashboard Duplicated",
  DashboardNameEdited = "Dashboard Name Edited",
  DashboardOKRCellClicked = "Dashboard OKR Cell Clicked",
  DashboardTemplateBrowsed = "Dashboard Template Browsed",
  DashboardTemplateViewed = "Dashboard Template Viewed",
  DashboardPptxExportStarted = "Dashboard PPTX Export Started",
  DashboardPptxExportFailed = "Dashboard PPTX Export Failed",
  DashboardPptxExportCompleted = "Dashboard PPTX Export Completed",
  ExportPreviewViewed = "Export Preview Viewed",
  DashboardPdfExportStarted = "Dashboard PDF Export Started",
  DashboardPdfExportFailed = "Dashboard PDF Export Failed",
  DashboardPdfExportCompleted = "Dashboard PDF Export Completed",
  WhatsNewCtaClicked = "Whats New CTA Clicked",
  WhatsNewModalClosed = "Whats New Modal Closed",
  WhatsNewDismissed = "Whats New Dismissed",
  SlackChannelsPageViewed = "Slack Channels Page Viewed",
  SlackChannelsJoined = "Slack Channels Joined",
  SlackInstallRetryClicked = "Slack Install Retry Clicked",
  SlackOnboardingCompleted = "Slack Onboarding Completed",
  SlackPublicMessageRhythmsClicked = "Slack Public Message Rhythms Clicked",
  ChatRateLimitErrorShown = "Chat Rate Limit Error Shown",
  ChatDictationMicClicked = "Chat Dictation Mic Clicked",
  ChatDictationStarted = "Chat Dictation Started",
  ChatDictationLiveCompleted = "Chat Dictation Live Completed",
  ChatDictationFallbackCompleted = "Chat Dictation Fallback Completed",
  ChatDictationAccepted = "Chat Dictation Accepted",
  ChatDictationCancelled = "Chat Dictation Cancelled",
  ChatDictationError = "Chat Dictation Error",
  ManageUsageLimitsClicked = "Manage Usage Limits Clicked",
  UpgradePlansClicked = "Upgrade Plans Clicked",
  UpgradeCheckoutStarted = "Upgrade Checkout Started",
  ViewPlansClicked = "View Plans Clicked",
  HelpEntryClicked = "help_entry_clicked",
  TeamHelpSaved = "team_help_saved",
  TeamHelpEdited = "team_help_edited",
}

/** Which DisplayOptions mount the mode switch happened in */
export type DisplayModeChangeSurface =
  | "okr_view"
  | "create_view_form"
  | "edit_view_form"
  | "reports_preview"

type ChatRateLimitErrorShownSource =
  | "chat_page"
  | "chat_side_panel"
  | "chat_full_view"

export interface DictationAnalyticsPayload {
  session_id?: string
  tenant_uuid?: string
  capture_mode?: "audio_worklet" | "media_recorder_fallback"
  phase?: "idle" | "connecting" | "listening" | "stopping" | "error"
  duration_ms?: number
  transcript_character_count?: number
  error_code?:
    | "auth_failed"
    | "invalid_frame"
    | "gateway_unavailable"
    | "upstream_error"
    | "finalization_timeout"
}

export type DictationAnalyticsEvent =
  | "mic_clicked"
  | "started"
  | "live_completed"
  | "fallback_completed"
  | "accepted"
  | "cancelled"
  | "error"

const DICTATION_ANALYTICS_EVENT_NAMES: Record<
  DictationAnalyticsEvent,
  EventName
> = {
  mic_clicked: EventName.ChatDictationMicClicked,
  started: EventName.ChatDictationStarted,
  live_completed: EventName.ChatDictationLiveCompleted,
  fallback_completed: EventName.ChatDictationFallbackCompleted,
  accepted: EventName.ChatDictationAccepted,
  cancelled: EventName.ChatDictationCancelled,
  error: EventName.ChatDictationError,
}

export function trackDictationAnalytics(
  event: DictationAnalyticsEvent,
  payload: DictationAnalyticsPayload
): void {
  track(DICTATION_ANALYTICS_EVENT_NAMES[event], payload)
}

type UpgradeFlowSource =
  | "sidenav"
  | "billing_page"
  | "plans_page"
  | "chat_error_banner"

/** Which "All plans" / "View all plans" billing link initiated navigation */
type ViewPlansClickedPlacement =
  | "billing_page_header_all_plans_link"
  | "billing_free_plan_promo_view_all_plans_link"

/** Which UI navigated to the plans page with upgrade intent */
type UpgradePlansClickedPlacement =
  | "billing_current_plan_plans_link"
  | "sidenav_free_plan_upgrade"
  | "chat_usage_limit_error_upgrade_link"
  | "chat_out_of_credits_billing_link"

/** Which control started Stripe Checkout for a plan upgrade */
export type UpgradeCheckoutStartedPlacement =
  | "billing_current_plan_checkout"
  | "billing_free_plan_promo_checkout"
  | "plans_page_pro_checkout"
  | "plans_page_business_checkout"
  | "plans_page_plan_checkout"

export type ChatEntryPointClickSource =
  /** The shell-owned ask bar docked along the bottom edge. */
  | "ask_bar"
  | "page_header"
  | "chat_header"
  | "main_nav"
  | "nav_overlay"
  | "icon_only_bar"
  | "all_chats_pane"
  | "settings_nav"
  | "settings_nav_overlay"
  | "settings_icon_only_bar"

export type ChatSidebarExpandClickSource =
  | "page_header"
  | "quick_view_header"
  | "chat_header"
  | "main_nav_icon_only_bar"
  | "settings_nav_icon_only_bar"
  | "inbox_header"
  | "exports_page"

export type ChatAllChatsOpenSource =
  | "main_nav"
  | "nav_overlay"
  | "icon_only_bar"
  | "settings_nav"
  | "settings_nav_overlay"
  | "settings_icon_only_bar"

export type ChatEntryPoint =
  | "human_request"
  | "ask_bar"
  | "blank_state_button"
  | "okr_context"
  | "okr_changes"
  | "dashboard_action"
  | "dashboard_blank_state"
  | "dashboard_onboarding"
  | "document_creation"
  | "pulse_creation"
  | "integration_setup"
  | "integration_manage"
  | "dashboard_templates"
  | "layouts"
  | "playbook_activation"
  | "playbook_execution"
  | "playbook_fix"
  | "document_refresh"
  | "html_document_refresh"
  | "document_playbook_setup"
  | "solution_hub_customize"
  | "chat_continue"
  | "chat_fork"
  | "operating_memory_node"
  | "rhythm_offer_customize"
  | "rhythm_offer_discuss"
  | "rhythm_offer_take_on"
  | "rhythm_offer_dry_run"
  | "rhythm_offer_show_steps"
  | "rhythm_offer_not_now"
  | "rhythm_offer_reject"
  | "rhythm_offer_not_mine"
  | "rhythm_offer_bring_back"
  | "rhythm_offer_cancel"

export interface ExportPreviewViewedProperties {
  exported_file_uuid: string
  file_type: "csv" | "docx" | "pptx" | "html"
  file_size_bytes?: number
  result: "success" | "empty" | "parse_error" | "unavailable"
  surface: "embedded" | "modal"
  preview_page_count?: number
  csv_row_count?: number
  csv_column_count?: number
  csv_has_more_rows?: boolean
  csv_has_more_columns?: boolean
  csv_has_truncated_cells?: boolean
}

interface EventConfig {
  [EventName.LibraryOrganization]: {
    event: string
    kind?: "move" | "archive" | "unarchive" | "undo" | "selection" | "sort"
    source?: "picker" | "drag"
    count?: number
    changed_count?: number
    requires_confirmation?: boolean
    direction?: "ASC" | "DESC"
  }
  [EventName.OTPVerificationPageViewed]: never
  [EventName.OTPSubmitted]: never
  [EventName.FreeTrialSignup]: never
  [EventName.UserLoggedIn]: never
  [EventName.OKRsImportInitiated]: never
  [EventName.OKRsImportCompleted]: never
  [EventName.OKRsImportFailed]: never
  [EventName.CascadeInitiated]: {
    objective_uuid: string
  }
  [EventName.QuickViewOpened]: {
    goal_uuid: string
    type: GoalType
  }
  [EventName.GoalDeleted]: {
    goal_uuid: string
    type: GoalType
    including_immediate_objectives: boolean
    including_immediate_initiatives: boolean
    including_immediate_key_results: boolean
    delete_hierarchy: boolean
  }
  [EventName.GoalDiscarded]: {
    goal_uuid: string
    type: GoalType
    including_immediate_objectives: boolean
    including_immediate_initiatives: boolean
    including_immediate_key_results: boolean
    delete_hierarchy: boolean
  }
  [EventName.GoalArchived]: {
    goal_uuid: string
    type: GoalType
    including_immediate_objectives: boolean
    including_immediate_initiatives: boolean
    including_immediate_key_results: boolean
    archive_hierarchy: boolean
  }
  [EventName.GoalUnarchived]: {
    goal_uuid: string
    type: GoalType
  }
  [EventName.GoalRestored]: {
    goal_uuid: string
    type: GoalType
  }
  [EventName.GoalPublished]: {
    goal_uuid: string
    type: GoalType
    including_immediate_children: boolean
    publish_hierarchy: boolean
  }
  [EventName.GoalApprovalRequested]: {
    goal_uuid: string
    type: GoalType
  }
  // mode_overridden: the mode came from a `?mode=` URL param (e.g. a
  // notification deep link) rather than the view's saved preferences.
  [EventName.OKRViewDisplayed]: {
    view_uuid: string
    display_mode: ViewMode
    is_default_view: boolean
    view_type: string
    mode_overridden: boolean
    board_template?: BoardSettings["template"]
  }
  [EventName.OKRDisplayModeChanged]: {
    from_mode: ViewMode
    to_mode: ViewMode
    surface: DisplayModeChangeSurface
  }
  [EventName.SearchResultsViewed]: {
    query: string
    results_count: number
    has_results: boolean
    source: "esearch"
    // "documents" is emitted when the global search "Documents" tab or
    // the documents section on the "All" tab reports its load.
    goal_type: GoalType | "all" | "documents"
  }
  [EventName.SearchResultClicked]: {
    query: string
    result_uuid: string
    // "Document" is used when a document row is clicked in global search;
    // the goal_type union is otherwise an OKR model class name.
    result_type: GoalType | "Document"
    result_position: number
    source: "esearch"
  }
  [EventName.ActivityFeedViewed]: {
    goal_uuid: string
    goal_type: GoalType
    records_count: number
    has_more: boolean
    source: "quick_view" | "page_view"
  }
  [EventName.ActivityFeedLoadMore]: {
    goal_uuid: string
    goal_type: GoalType
  }
  [EventName.ActivityFeedItemExpanded]: {
    goal_uuid: string
    goal_type: GoalType
    event_uuid: string
    event_type: string
    expand_source: "timeline_event" | "checkin_group" | "summary_group"
  }
  [EventName.ChatModelSwitched]: {
    model_id: string
    model_name: string
    model_provider: "anthropic" | "openai" | "google" | "xai"
    model_tier: "premium" | "balanced" | "fast"
    is_recommended: boolean
    is_preview: boolean
    previous_model_id: string
  }
  [EventName.ChatAutoPromptSent]: {
    entry_point: ChatEntryPoint
    entry_message?: string
  }
  [EventName.AIChatMessageSent]: {
    thread_message_number: number
    message_length: number
    model_version?: string
    chat_surface: ChatSurfaceAnalyticsValue
    chat_side?: ChatSideAnalyticsValue
    send_origin: "composer" | "queue"
  }
  // Queued items that get sent are counted on AI Chat Message Sent
  // (send_origin: "queue"); this event covers the rest of the queue's use.
  [EventName.AIChatMessageQueueAction]: {
    action: "queued" | "edited" | "deleted" | "reordered" | "resumed"
    /** Items in this thread's queue after the action. */
    queue_length: number
    chat_surface: ChatSurfaceAnalyticsValue
    /** Why the queue was paused, on "resumed" only. */
    hold_reason?: "mounted" | "stopped" | "errored" | "editing" | "other_tab"
  }
  [EventName.ChatLayoutObserved]: {
    chat_side: "left" | "right"
    chat_open: boolean
  }
  [EventName.ChatSideChanged]: {
    from_side: "left" | "right"
    to_side: "left" | "right"
  }
  [EventName.ChatFullViewEntered]: {
    chat_side: "left" | "right"
  }
  [EventName.ChatAllChatsOpened]: {
    chat_side: "left" | "right"
    source: ChatAllChatsOpenSource
  }
  [EventName.ChatFullViewNavigationUsed]: {
    chat_side: "left" | "right"
    navigation_source: "sidebar" | "onboarding_menu_item" | "search"
  }
  [EventName.ChatStarterSelected]: {
    action: string
    context_type: "selection" | "document" | "okr" | "playbook" | "general"
    availability: "unknown" | "loading" | "ready" | "empty" | "error"
  }
  [EventName.ChatStartersShown]: {
    actions: string[]
    context_type: "selection" | "document" | "okr" | "playbook" | "general"
    availability: "unknown" | "loading" | "ready" | "empty" | "error"
  }
  [EventName.ChatStarterSent]: {
    action: string
    context_type: "selection" | "document" | "okr" | "playbook" | "general"
  }
  [EventName.ChatEntryPointClicked]: {
    entry_point: "ask_bar" | "ask_rhythms" | "new_chat"
    source: ChatEntryPointClickSource
    /** Ask bar only: whether the message carried page context. */
    had_context?: boolean
  }
  [EventName.ChatStarterSelected]: {
    action: string
    context_type: "selection" | "document" | "okr" | "playbook" | "general"
    availability: "unknown" | "loading" | "ready" | "empty" | "error"
  }
  [EventName.ChatStartersShown]: {
    actions: string[]
    context_type: "selection" | "document" | "okr" | "playbook" | "general"
    availability: "unknown" | "loading" | "ready" | "empty" | "error"
  }
  [EventName.ChatStarterSent]: {
    action: string
    context_type: "selection" | "document" | "okr" | "playbook" | "general"
  }
  [EventName.ChatSidebarExpandClicked]: {
    source: ChatSidebarExpandClickSource
  }
  [EventName.ChatFullViewToggleClicked]: {
    action: "maximize" | "minimize"
    source: "chat_header"
    chat_side: "left" | "right"
  }
  [EventName.ChatMessageCopied]: {
    message_role: "user" | "assistant"
    has_goal_cards: boolean
    has_inline_mentions: boolean
  }
  [EventName.ChatSearchFocused]: {
    source: "all_chats_pane"
  }
  [EventName.ChatSearchQueried]: {
    query: string
    source: "all_chats_pane"
  }
  [EventName.ChatSearchResultClicked]: {
    query: string
    source: "all_chats_pane"
  }
  [EventName.DashboardVersionViewed]: {
    dashboard_uuid: string
    version_id: number
  }
  [EventName.DashboardVersionRestored]: {
    dashboard_uuid: string
    version_id: number
  }
  [EventName.ActivityLogOpened]: {
    dashboard_uuid: string
  }
  [EventName.ActivityLogEntryExpanded]: {
    dashboard_uuid: string
  }
  [EventName.DashboardViewed]: {
    dashboard_uuid: string
    dashboard_name: string
    is_owner: boolean
    permission_level: string
    has_template: boolean
    template_uuid?: string
  }
  [EventName.ExportPreviewViewed]: ExportPreviewViewedProperties
  [EventName.DashboardListViewed]: {
    filter: "all" | "created_by_me" | "shared_with_me"
  }
  [EventName.DashboardContentEdited]: {
    dashboard_uuid: string
    edit_source: "inline"
  }
  [EventName.DashboardChartEdited]: {
    dashboard_uuid: string
    chart_type: string
    change_type:
      | "type_changed"
      | "color_changed"
      | "series_changed"
      | "title_changed"
  }
  [EventName.DashboardExported]: {
    dashboard_uuid: string
    export_format: "docx" | "pptx" | "pdf"
  }
  [EventName.DashboardDuplicated]: {
    original_dashboard_uuid: string
    new_dashboard_uuid: string
  }
  [EventName.DashboardNameEdited]: {
    dashboard_uuid: string
    old_name: string
    new_name: string
  }
  [EventName.DashboardOKRCellClicked]: {
    dashboard_uuid: string
    goal_uuid: string
    goal_type: string
  }
  [EventName.DashboardTemplateBrowsed]: {
    templates_count: number
    active_templates_count: number
  }
  [EventName.DashboardTemplateViewed]: {
    template_uuid: string
    template_name: string
    is_active: boolean
  }
  [EventName.DashboardPptxExportStarted]: {
    dashboard_uuid: string
    template_source: "uploaded" | "library" | "none"
    has_guidance: boolean
  }
  [EventName.DashboardPptxExportFailed]: {
    dashboard_uuid: string
    template_source: "uploaded" | "library" | "none"
    error_message: string
  }
  [EventName.DashboardPptxExportCompleted]: {
    dashboard_uuid: string
    template_source: "uploaded" | "library" | "none"
  }
  [EventName.DashboardPdfExportStarted]: {
    dashboard_uuid: string
  }
  [EventName.DashboardPdfExportFailed]: {
    dashboard_uuid: string
    error_message: string
  }
  [EventName.DashboardPdfExportCompleted]: {
    dashboard_uuid: string
  }
  [EventName.WhatsNewCtaClicked]: {
    announcement_key: string
    variant: "expanded" | "icon_only"
  }
  [EventName.WhatsNewModalClosed]: {
    announcement_key: string
    close_method: "got_it" | "backdrop_or_escape"
  }
  [EventName.WhatsNewDismissed]: {
    announcement_key: string
    variant: "expanded" | "icon_only"
  }
  // Slack onboarding channel-picker funnel. failure_reason values mirror the
  // Rails connector failure enum (e.g. "workspace_already_connected",
  // "access_denied", "oauth_error"); unrecognized values are sent as "unknown".
  [EventName.SlackChannelsPageViewed]: {
    install_failed: boolean
    failure_reason?: string
  }
  [EventName.SlackChannelsJoined]: {
    channels_selected: number
    channels_requested: number
    queued: boolean
    mode: "all" | "select"
  }
  [EventName.SlackInstallRetryClicked]: {
    failure_reason?: string
  }
  [EventName.SlackOnboardingCompleted]: {
    destination: "slack" | "app"
    continued_without_slack: boolean
    failure_reason?: string
    installation_id?: string
  }
  [EventName.SlackPublicMessageRhythmsClicked]: {
    installation_id: string
  }
  [EventName.ChatRateLimitErrorShown]: {
    source: ChatRateLimitErrorShownSource
  }
  [EventName.ChatDictationMicClicked]: DictationAnalyticsPayload
  [EventName.ChatDictationStarted]: DictationAnalyticsPayload
  [EventName.ChatDictationLiveCompleted]: DictationAnalyticsPayload
  [EventName.ChatDictationFallbackCompleted]: DictationAnalyticsPayload
  [EventName.ChatDictationAccepted]: DictationAnalyticsPayload
  [EventName.ChatDictationCancelled]: DictationAnalyticsPayload
  [EventName.ChatDictationError]: DictationAnalyticsPayload
  [EventName.ManageUsageLimitsClicked]: {
    source: "chat_error_banner"
  }
  [EventName.UpgradePlansClicked]: {
    source: UpgradeFlowSource
    placement: UpgradePlansClickedPlacement
  }
  [EventName.UpgradeCheckoutStarted]: {
    source: UpgradeFlowSource
    placement: UpgradeCheckoutStartedPlacement
  }
  [EventName.ViewPlansClicked]: {
    source: UpgradeFlowSource
    placement: ViewPlansClickedPlacement
  }
  // value_kind is the type derived from the entry's free-text value at click
  // time, so we can see which target types teams actually use. surface tells
  // the two nav widths apart, since the collapsed rail shows only a favicon.
  [EventName.HelpEntryClicked]: {
    value_kind: TeamHelpValueKind
    surface: "sidebar" | "collapsed_rail"
  }
  [EventName.TeamHelpSaved]: {
    entry_count: number
  }
  [EventName.TeamHelpEdited]: {
    entry_count: number
  }
}

export const track = <T extends EventName>(
  type: T,
  ...args: EventConfig[T] extends never ? [] : [properties: EventConfig[T]]
) => {
  if (!analytics) return
  const globalProps = getGlobalProperties()
  void analytics.track(type, { ...globalProps, ...args[0] })
}

export const analyticsIdentify = (
  account: CurrentAccount,
  user: CurrentUser
) => {
  const emailDomain = String(account.email.split("@")[1])
  const plgOnboarded = user.tenant?.setting?.plg_onboarded ?? false
  tenantContext = {
    tenant_uuid: user.tenant_uuid,
    tenant_name: user.tenant?.display_name ?? "",
    plg_onboarded: plgOnboarded,
    internal: isInternalEmail(account.email),
  }

  if (!analytics) return
  void analytics.identify(user.uuid, {
    tenant_uuid: user.tenant_uuid,
    account_id: user.account_id,
    email_domain: emailDomain,
    internal: isInternalEmail(account.email),
  })
  void analytics.group(user.tenant_uuid, {
    account_id: user.account_id,
    name: user.tenant?.display_name,
    plg_onboarded: user.tenant?.setting?.plg_onboarded ?? false,
  })
}

export const analyticsReset = () => {
  tenantContext = null
  if (!analytics) return
  void analytics.reset()
}

/**
 * Routes where we have dedicated track() events that replace the automatic
 * page() call. Adding a pattern here prevents the SegmentAnalytics component
 * from firing a duplicate page event for that route.
 *
 * Patterns are matched against the pathname:
 *  - Exact strings match exactly (e.g. "/dashboards")
 *  - RegExp patterns match via .test() (e.g. /^\/dashboards\/[^/]+$/)
 */
const SUPPRESS_PAGE_TRACKING_PATTERNS: (string | RegExp)[] = [
  "/dashboards",
  /^\/dashboards\/[^/]+$/,
]

function shouldSuppressPageTracking(pathname: string): boolean {
  return SUPPRESS_PAGE_TRACKING_PATTERNS.some((pattern) =>
    typeof pattern === "string" ? pathname === pattern : pattern.test(pathname)
  )
}

export function SegmentAnalytics() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!pathname) return
    if (shouldSuppressPageTracking(pathname)) return

    // Defer until after the browser paints so DocumentTitle (driven by
    // usePageTitle / Zustand) has flushed the updated <title> to the DOM.
    const rafId = requestAnimationFrame(() => {
      const fullUrl = `${window.location.origin}${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      const globalProps = getGlobalProperties()
      void analytics?.page({
        ...globalProps,
        page_name: document.title.replace(/ - Rhythms$/, ""),
        page_path: pathname,
        page_url: fullUrl,
        page_title: document.title,
        path: pathname,
        url: fullUrl,
        referrer: document.referrer,
      })
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [pathname, searchParams])

  return null
}
